/*
  Shared ROI Math Utilities
  ------------------------------------------------------------
  Centralizes reusable math for ROI calculations and agent classification
  to avoid duplication across components and report generators.
*/

/** Caps and constants */
export const TIME_SAVED_CAP = 0.60;       // max 60% total FTE time savings from agents
export const REVENUE_IMPACT_CAP = 0.05;   // max +5% revenue impact from agents
export const HOURS_PER_FTE = 2080;        // 40 hrs * 52 wks

/**
 * Compute automation factor used for scaling agent-driven impact.
 * Maps 1..10 to ~0.60 -> 0.15 (consistent with ROICalculator).
 */
export function computeAutomationFactor(automationMaturity) {
  return 0.60 - ((automationMaturity - 1) / 9) * 0.45; // 0.60 -> 0.15
}

/**
 * Compute industry waste percent based on automation maturity (1..10)
 * 1 = best automation -> wasteLow, 10 = worst -> wasteHigh
 */
export function computeIndustryWastePercent(industry, automationMaturity) {
  if (!industry) return 0;
  return industry.wasteLow + ((10 - automationMaturity) / 9) * (industry.wasteHigh - industry.wasteLow);
}

/**
 * Compute admin waste from process maturity (1..10)
 * 1 -> 30%, 10 -> 20%
 */
export function computeAdminWasteFromProcess(processMaturity) {
  const adminWaste = 0.30 - ((processMaturity - 1) / 9) * 0.10; // 0.30 down to 0.20
  return { low: adminWaste, high: adminWaste };
}

/**
 * AI Agents Average savings using fixed admin waste bounds 20%..30%.
 */
export function computeAIAgentsAverage(industry, annualRevenue) {
  const aiAdminLow = 0.20;
  const aiAdminHigh = 0.30;
  return {
    percentLow: industry.wasteLow * aiAdminLow,
    percentHigh: industry.wasteHigh * aiAdminHigh,
    amountLow: annualRevenue * (industry.wasteLow * aiAdminLow),
    amountHigh: annualRevenue * (industry.wasteHigh * aiAdminHigh)
  };
}

/**
 * Interpolate Potential Annual Savings within [AI base low, AI base high]
 * using both sliders so that (1,1) returns the high end.
 */
export function computePotentialSavings(aiAgentsAverage, automationMaturity, processMaturity) {
  const baseLowAmount = aiAgentsAverage.amountLow;
  const baseHighAmount = aiAgentsAverage.amountHigh;
  const autoNorm = (10 - automationMaturity) / 9; // 1 at level 1, 0 at level 10
  const procNorm = (10 - processMaturity) / 9;    // 1 at level 1, 0 at level 10
  const sLow = Math.min(autoNorm, procNorm);
  const sHigh = Math.max(autoNorm, procNorm);
  return {
    low: baseLowAmount + (baseHighAmount - baseLowAmount) * sLow,
    high: baseLowAmount + (baseHighAmount - baseLowAmount) * sHigh
  };
}

/**
 * Create a maturity threshold and classify agents by implemented vs opportunity.
 * Rule: T = 11 - automationMaturity; implemented if score <= T, else opportunity.
 */
export function classifyAgentsByMaturity(automationMaturity, agents) {
  // New rule: tighter implemented band at low maturity values so level 2 does NOT mark most agents implemented
  // T = clamp(automationMaturity - 1, 1..10)
  const T = Math.max(1, Math.min(10, (automationMaturity || 0) - 1));
  const implemented = [];
  const opportunities = [];
  (agents || []).forEach(a => {
    const score = typeof a?.score === 'number' ? a.score : 5;
    if (score <= T) implemented.push(a); else opportunities.push(a);
  });
  return { threshold: T, implemented, opportunities };
}

/** Normalize an array of positive numbers to sum 1. Returns {weights, sum}. */
export function normalizeWeights(values) {
  const sum = (values || []).reduce((s, v) => s + (isFinite(v) && v > 0 ? v : 0), 0);
  if (!(sum > 0)) return { weights: values.map(_ => 0), sum: 0 };
  return { weights: values.map(v => (isFinite(v) && v > 0 ? v / sum : 0)), sum };
}

/**
 * Proportional allocation for a list of items with given weights.
 * Returns list of { low, high } amounts that sum to totals.
 */
export function proportionalAllocate(totalLow, totalHigh, weights) {
  const { weights: norm, sum } = normalizeWeights(weights);
  if (!(sum > 0)) return weights.map(_ => ({ low: 0, high: 0 }));
  return norm.map(w => ({ low: totalLow * w, high: totalHigh * w }));
}

/** Guard: safe divide returning 0 on invalid */
export function safeDiv(a, b) {
  return b && isFinite(b) ? a / b : 0;
}
